import { useEffect, useState } from 'react'
import { useFlowReferenceData } from '@forwardflow/features/campaign-flow/hooks/useFlowReferenceData'
import { useNavigate, useParams } from 'react-router-dom'
import { FlowShell } from '@forwardflow/features/campaign-flow/components/frame/FlowShell'
import { TopBar } from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import { ActivityPanel } from '@forwardflow/features/campaign-flow/components/stats/ActivityPanel'
import {
   PeriodSwitch,
   ResultsBlock,
} from '@forwardflow/features/campaign-flow/components/stats/ResultsBlock'
import { KindTag } from '@forwardflow/features/campaign-flow/components/ui/KindTag'
import { AiOrb } from '@forwardflow/features/campaign-flow/components/ui/AiOrb'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   Card,
   Pill,
   SmallButton,
   Spinner,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import { useFlowBase } from '@forwardflow/features/campaign-flow/hooks/useFlowBase'
import { useVoiceOption } from '@forwardflow/features/campaign-flow/hooks/useFlowHelpers'
import {
   ICampaignStats,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import { compact } from '@forwardflow/features/campaign-flow/utils/format'
import { conversationsLink } from '@forwardflow/features/campaign-flow/utils/links'
import '@forwardflow/features/campaign-flow/components/campaign-flow.css'

/** One campaign: the same results as the Campaigns page, its setup, and what needs a person. */
export const CampaignFlowLive = () => {
   useFlowReferenceData()
   const { id = '' } = useParams()
   const navigate = useNavigate()
   const { base } = useFlowBase()
   const draft = useCampaignFlowStore((s) => s.draft)
   const voice = useVoiceOption()
   const [period, setPeriod] = useState<TPeriod>('30d')
   const [stats, setStats] = useState<ICampaignStats | null>(null)
   const [paused, setPaused] = useState(false)
   const [error, setError] = useState<string | null>(null)
   useEffect(() => {
      setStats(null)
      void campaignFlowService
         .campaignStats(id, period)
         .then((s) => {
            setStats(s)
            setPaused(s.status === 'paused')
         })
         .catch((e: Error) =>
            setError(e.message || 'Could not load this campaign.')
         )
   }, [id, period])
   const isThis = draft.id === id
   const name = stats?.name || (isThis ? draft.audience.name : 'Campaign')
   const edit = (content: boolean) => {
      navigate(
         `${base}/${encodeURIComponent(id)}/edit?section=${content ? 'content' : 'cadence'}`
      )
   }

   const enrolled =
      isThis && draft.audience.leadCount
         ? draft.audience.leadCount
         : (stats?.enrolled ?? 0)

   return (
      <FlowShell className="lg:!overflow-hidden">
         {/* Fits the screen on a laptop: the activity list scrolls inside, the page does not. */}
         <div
            className="cf flex flex-col gap-4 pb-6 lg:pb-4 lg:h-full min-h-0"
            data-testid="campaign-flow-live"
         >
            <TopBar
               name={name}
               right={
                  <>
                     <span className="hidden md:block">
                        <PeriodSwitch value={period} onChange={setPeriod} />
                     </span>
                     <Pill
                        tone={paused ? 'neutral' : 'live'}
                        testId="cf-live-status"
                     >
                        ● {paused ? 'Paused' : 'Live'}
                     </Pill>
                     <SmallButton
                        icon={paused ? 'play' : 'pause'}
                        onClick={() => {
                           const next = !paused
                           setPaused(next)
                           void campaignFlowService
                              .setPaused(id, next)
                              .catch(() => setPaused(!next))
                        }}
                        testId="cf-live-pause"
                     >
                        {paused ? 'Resume' : 'Pause'}
                     </SmallButton>
                  </>
               }
            />
            <Card className="px-5 md:px-6 py-4 flex flex-col md:flex-row items-center gap-4">
               <AiOrb
                  hue={stats?.voiceHue ?? voice.hue}
                  size={56}
                  talking={!paused}
               />
               <div className="flex-1 text-center md:text-left min-w-0">
                  <KindTag
                     kind={stats?.kind}
                     className="mb-1"
                     testId="cf-live-kind"
                  />
                  <h1 className="text-xl leading-7 font-semibold tracking-[-.02em]">
                     {paused
                        ? 'Paused'
                        : `${stats?.voiceName || (isThis && draft.voice.name) || 'Your assistant'} is working ${compact(enrolled)} leads`}
                  </h1>
                  <p className="text-[13px] text-[#9ca3af]">
                     {paused
                        ? 'No calls or texts go out until you resume.'
                        : 'Calls go out during your calling hours.'}
                  </p>
               </div>
               <div className="flex flex-wrap justify-center gap-2">
                  <span className="md:hidden">
                     <PeriodSwitch
                        value={period}
                        onChange={setPeriod}
                        testId="cf-period-m"
                     />
                  </span>
                  <SmallButton
                     icon="phone"
                     onClick={() => edit(true)}
                     testId="cf-live-test"
                  >
                     Review content
                  </SmallButton>
                  <SmallButton
                     icon="pen"
                     variant="outline"
                     onClick={() => edit(false)}
                     testId="cf-live-edit"
                  >
                     Edit campaign
                  </SmallButton>
               </div>
            </Card>

            {error && (
               <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                  {error}
               </div>
            )}
            {!stats ? (
               !error && (
                  <div className="flex justify-center py-8">
                     <Spinner size={22} />
                  </div>
               )
            ) : (
               <>
                  <ResultsBlock
                     stats={stats}
                     leadsFootnote={`of ${compact(enrolled)} leads in the campaign`}
                     testId="cf-live-results"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start lg:items-stretch flex-1 min-h-0">
                     <SetupCard stats={stats} onEdit={() => edit(false)} />
                     <ActivityPanel
                        fill
                        campaignId={id}
                        open={(kind, leadId) =>
                           navigate(
                              conversationsLink({
                                 campaignId: id,
                                 campaignName: name,
                                 leadId,
                                 kind,
                              })
                           )
                        }
                     />
                  </div>
               </>
            )}
         </div>
      </FlowShell>
   )
}

/* ---------- What it is set up to do ---------- */

const SetupCard = ({
   stats,
   onEdit,
}: {
   stats: ICampaignStats
   onEdit: () => void
}) => {
   const rows: Array<{ icon: TFlowIcon; label: string; value: string }> = [
      { icon: 'users', label: 'Who it calls', value: stats.setup.audience },
      { icon: 'cal', label: 'Timing', value: stats.setup.timing },
      { icon: 'voice', label: 'Voice', value: stats.voiceName || 'Not set' },
      {
         icon: 'phone',
         label: 'Calls from',
         value: stats.setup.numbers.length
            ? stats.setup.numbers.join(' · ')
            : 'No numbers yet',
      },
      { icon: 'zap', label: 'Hot leads go to', value: stats.setup.handoff },
   ]
   return (
      <Card
         className="overflow-hidden lg:overflow-y-auto cf-scroll min-h-0"
         testId="cf-live-setup"
      >
         <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
            <span className="text-sm font-semibold">How it is set up</span>
            <button
               type="button"
               onClick={onEdit}
               className="ml-auto text-[13px] font-semibold text-[#DAC2FF] hover:text-white"
               data-testid="cf-live-setup-edit"
            >
               Change
            </button>
         </div>
         <div className="divide-y divide-white/[0.05]">
            {rows.map((r) => (
               <div
                  key={r.label}
                  className="flex items-start gap-3 px-4 py-3 text-[13px]"
               >
                  <span className="w-7 h-7 rounded-lg bg-[#9061DA]/15 text-[#DAC2FF] flex items-center justify-center shrink-0">
                     <FlowIcon name={r.icon} size={13} />
                  </span>
                  <span className="min-w-0">
                     <span className="block text-xs text-[#9ca3af]">
                        {r.label}
                     </span>
                     <span className="block font-medium">{r.value}</span>
                  </span>
               </div>
            ))}
         </div>
      </Card>
   )
}
